import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Lock, Clock, DollarSign, Check } from 'lucide-react';
import { formatCurrency, validatePin, parseAccountBalance } from '../utils/helpers';
import axios from 'axios';
import { useUser } from '../context/UserContext';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const API_BASE = 'https://tradebot-k4nt.onrender.com/api';

const PeneInvestmentPlans = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [activePlans, setActivePlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [amount, setAmount] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { user: contextUser } = useUser();

  useEffect(() => {
    if (!contextUser || !contextUser.telegramName) {
      navigate('/login');
      return;
    }
    setUser(contextUser);
    fetchPlans();
    fetchActivePlans(contextUser.telegramName);
  }, [contextUser, navigate]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_BASE}/payment/GetSubPlanes-web`);
      if (response.data?.planes) {
        setPlans(response.data.planes);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchActivePlans = async (username) => {
    try {
      const response = await axios.get(`${API_BASE}/payment/GetSubPlanes-web/${username}`);
      if (response.data?.subData) {
        setActivePlans(response.data.subData);
      }
    } catch (error) {
      console.error('Error fetching active plans:', error);
    }
  };

  const handleSubscribe = (plan) => {
    setSelectedPlan(plan);
    setAmount(plan.minAmount.toString());
    setShowModal(true);
  };

  const confirmSubscription = async (e) => {
    e.preventDefault();

    if (!validatePin(pin)) {
      toast.error('Please enter a valid 6-digit PIN');
      return;
    }

    if (parseFloat(amount) < selectedPlan.minAmount) {
      toast.error(`Minimum amount is ${formatCurrency(selectedPlan.minAmount)}`);
      return;
    }

    if (parseFloat(amount) > parseAccountBalance(user?.accountBalance)) {
      toast.error('Insufficient balance');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE}/payment/SubscribeToPlan-web`, {
        telegramName: user.telegramName,
        planId: selectedPlan.id,
        amount: parseFloat(amount),
        pin: pin
      });

      if (response.status === 200 || response.status === 201) {
        toast.success(`Successfully subscribed to ${selectedPlan.name}!`);
        fetchActivePlans(user.telegramName);
        setShowModal(false);
        setPin('');
        setAmount('');
        setSelectedPlan(null);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.error || 'Subscription failed');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">PENE Investment Plans</h1>
          <p className="text-slate-600">Choose a plan and start earning passive income</p>
        </div>

        {activePlans.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-8">
            <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Check className="text-green-500" />
              Your Active Plans
            </h3>
            <div className="grid gap-4">
              {activePlans.map((plan, index) => (
                <div key={index} className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-slate-900">{plan.planName}</p>
                      <p className="text-sm text-slate-600">{plan.roi}% ROI • {plan.duration} days</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-600">Invested</p>
                      <p className="text-xl font-bold text-green-600">{formatCurrency(plan.amount)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-lg border-2 border-slate-200 p-6 hover:border-purple-400 hover:shadow-xl transition-all"
            >
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-4">
                  <TrendingUp className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                <p className="text-slate-600 text-sm">{plan.description}</p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <span className="text-slate-600 flex items-center gap-2">
                    <TrendingUp size={16} />
                    ROI
                  </span>
                  <span className="font-bold text-purple-600">{plan.roi}%</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <span className="text-slate-600 flex items-center gap-2">
                    <Clock size={16} />
                    Duration
                  </span>
                  <span className="font-bold text-blue-600">{plan.duration} days</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="text-slate-600 flex items-center gap-2">
                    <DollarSign size={16} />
                    Minimum
                  </span>
                  <span className="font-bold text-green-600">{formatCurrency(plan.minAmount)}</span>
                </div>
              </div>

              <button
                onClick={() => handleSubscribe(plan)}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
              >
                Subscribe Now
              </button>
            </div>
          ))}
        </div>

        {showModal && selectedPlan && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Subscribe to {selectedPlan.name}</h2>

              <div className="mb-6 p-4 bg-purple-50 rounded-xl space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">ROI:</span>
                  <span className="font-bold text-purple-600">{selectedPlan.roi}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Duration:</span>
                  <span className="font-bold">{selectedPlan.duration} days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Min Amount:</span>
                  <span className="font-bold text-green-600">{formatCurrency(selectedPlan.minAmount)}</span>
                </div>
              </div>

              <form onSubmit={confirmSubscription} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Investment Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    min={selectedPlan.minAmount}
                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Enter PIN
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                    <input
                      type="password"
                      placeholder="6-digit PIN"
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      className="w-full pl-11 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-center tracking-widest"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setPin('');
                      setSelectedPlan(null);
                    }}
                    className="py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-all disabled:opacity-50"
                  >
                    {loading ? 'Processing...' : 'Confirm'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PeneInvestmentPlans;
